import React from 'react';
import { CheckIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/ui/CtaBand';
import { milestones } from '../data/record';
const outcomes = [{
  title: 'A published scorecard',
  detail: 'An independently verified report on every hundred-day commitment, including the ones that were missed and why.'
}, {
  title: 'Named accountability',
  detail: 'Every commitment carries the name of the officer responsible for it, published on the delivery portal.'
}, {
  title: 'An early budget framework',
  detail: 'The 2028 fiscal framework released ahead of schedule so the National Assembly and the public can scrutinise it properly.'
}];
const dependencies = ['Cooperation from the National Assembly on the state policing and power reform bills', 'State governments signing delivery compacts tied to federal funding', 'Recruitment capacity for the first tranche of rural security officers', 'Data quality from federal agencies for the baseline measurements'];
export function FirstHundredDays() {
  return <>
      <PageHero eyebrow="Before anything else" title="The first 100 days" intro="What this administration does before it asks to be judged on anything else — dated, costed and published in advance." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'First 100 Days'
    }]} data-id="element-935" data-mp="1ft7m9ilag7zz" />

      <section className="bg-sand-50 py-16 lg:py-24" aria-labelledby="phases-heading" data-id="element-936" data-mp="1kski2su50pep">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-937" data-mp="1zldxyfdbn7jw">
          <SectionHeading eyebrow="The sequence" title="Four phases, in order" intro="Each phase builds the capability the next one depends on." data-id="element-938" data-mp="1ulj6a9lq2dxs" />

          <ol className="mt-12 space-y-6" data-id="element-939" data-mp="23m7n1c2i2sed">
            {milestones.map((milestone, index) => <li key={milestone.marker} data-id="element-940" data-mp="118jmd6t2eoyv" data-mpi={index}>
                <Reveal delay={Math.min(index, 3) * 0.04} data-id="element-941" data-mp="115njd42d1a3k">
                  <article className="grid gap-6 rounded-media border border-sand-200 bg-white p-7 lg:grid-cols-12 lg:p-9" data-id="element-942" data-mp="167czgy4fwdyi">
                    <div className="lg:col-span-4" data-id="element-943" data-mp="1a627scamf5ba">
                      <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-green-700" data-id="element-944" data-mp="1ez52inr4wkt2">
                        Phase {index + 1}
                      </p>
                      <h3 className="mt-2 font-display text-[26px] leading-tight text-ink" data-id="element-945" data-mp="1ox2qda530fl5">
                        {milestone.marker}
                      </h3>
                      <p className="mt-2 font-display text-[18px] text-gold-600" data-id="element-946" data-mp="1twntzxv75c47">
                        {milestone.title}
                      </p>
                    </div>
                    <div className="lg:col-span-8" data-id="element-947" data-mp="3vokbrp7yji7f">
                      <p className="text-[16px] leading-relaxed text-slate-700" data-id="element-948" data-mp="3vpvctha0qbuu">
                        {milestone.description}
                      </p>
                      <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-949" data-mp="3s6d3kicxi5mz">
                        {milestone.priorities.map(priority => <li key={priority} className="flex gap-2.5 rounded-lg bg-sand-50 px-4 py-3 text-[14px] text-slate-700" data-id="element-950" data-mp="3nd1su64zx4ps">
                            <CheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-green-600" data-id="element-951" data-mp="eyt8tmgjhm98" />
                            {priority}
                          </li>)}
                      </ul>
                    </div>
                  </article>
                </Reveal>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="outcomes-heading" data-id="element-952" data-mp="2qpx9shmpuh7h">
        <div className="mx-auto grid max-w-content gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-953" data-mp="2lny4h27f1a7w">
          <div className="lg:col-span-7" data-id="element-954" data-mp="df9u4deg496c">
            <SectionHeading eyebrow="Expected outcomes" title="What Nigerians should see by day 100" data-id="element-955" data-mp="i7jhblo6ev4x" />
            <ul className="mt-8 divide-y divide-sand-300 border-y border-sand-300" data-id="element-956" data-mp="4gelkunximzv">
              {outcomes.map(outcome => <li key={outcome.title} className="py-6" data-id="element-957" data-mp="8irrpk6l8mx6">
                  <h3 className="font-display text-[20px] text-ink" data-id="element-958" data-mp="w7az7xhtcvo7">{outcome.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-959" data-mp="w5hrsfzdm7f5">
                    {outcome.detail}
                  </p>
                </li>)}
            </ul>
          </div>
          <div className="lg:col-span-5" data-id="element-960" data-mp="s5pejpz1fd3g">
            <div className="rounded-media border border-sand-200 bg-sand-100 p-7" data-id="element-961" data-mp="s4o49npuiums">
              <h2 className="font-display text-[22px] text-ink" data-id="element-962" data-mp="37wlq13e64o9p">
                What this depends on
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-963" data-mp="3cwpws1p9y4mq">
                Stating dependencies openly is part of the standard. These are the things outside
                the Presidency’s sole control.
              </p>
              <ul className="mt-5 space-y-3" data-id="element-964" data-mp="30fotxa0h6brd">
                {dependencies.map(item => <li key={item} className="flex gap-2.5 text-[14px] leading-relaxed text-slate-700" data-id="element-965" data-mp="3ear9ypz1dhhw">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" data-id="element-966" data-mp="39c7o0pkg05pj" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Every policy area has its own hundred-day commitments." description="Read them in full, policy by policy, with the funding logic behind each one." primary={{
      label: 'Explore the agenda',
      to: '/agenda'
    }} secondary={{
      label: 'Read the manifesto',
      to: '/manifesto'
    }} data-id="element-967" data-mp="2qnj1y0a83uc6" />
    </>;
}